import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { BRAND, PAGE, QUERY, SALESCODE_KEY } from "../common/Constants";
import { setMsalAccount } from "./msalAccount";
import AuthenticationClient from "../clients/AuthenticationClient";
import { logMsg } from "../common/Logger";
import { is401, is500 } from "../errors/ErrorConstants";
import { FORD_BRAND, LINCOLN_BRAND } from "../components/accessories/Brands";
import App from "../apps/App";
import Loading from "../shared/loading/Loading";
import ErrorScreen from "../errors/ErrorScreen";
import { getBackendScopes } from "./authConfig";
import { isLikelyJwt } from "./tokenShape";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { getThemeByBrand } from "../utils/themeUtil";
import { getFieldSupportBrand } from "../utils/fieldSupportBrand";

const AUTH_ERRORS = {
  PAGE_ERROR: "PAGE_ERROR",
  ACCESS_DENIED: "ACCESS_DENIED",
  AUTH_FAILED: "AUTH_FAILED",
};

const readParam = (sessionKey, urlKey = sessionKey) =>
  sessionStorage.getItem(sessionKey) ||
  new URLSearchParams(globalThis.location.search).get(urlKey);

const buildAuthenticationRequest = () => {
  const request = {};
  const salesCode = readParam(SALESCODE_KEY, "salesCode");
  const page = readParam(PAGE);
  const query = readParam(QUERY);
  const brand = readParam(BRAND);

  if (salesCode) request.salesCode = salesCode;
  if (page) request.page = page;
  if (query) request.query = query;
  if (brand) request.brand = brand;

  return { request, salesCode };
};

const persistUserDetails = (userDetails) => {
  if (userDetails.salesCode) {
    sessionStorage.setItem(SALESCODE_KEY, userDetails.salesCode);
  }

  if (userDetails.dealerId) {
    sessionStorage.setItem("dealerId", userDetails.dealerId);
  }

  if (userDetails.market) {
    sessionStorage.setItem("market", userDetails.market);
  }
};

const resolveBrand = (salesCodeFromInput, userDetails) => {
  if (userDetails.userType === "field_view") {
    return getFieldSupportBrand(userDetails.divisionCode);
  }

  const resolvedSalesCode = salesCodeFromInput || userDetails.salesCode;

  if (!resolvedSalesCode) {
    return FORD_BRAND;
  }

  return resolvedSalesCode.startsWith(LINCOLN_BRAND)
    ? LINCOLN_BRAND
    : FORD_BRAND;
};

const mapErrorToState = (error) => {
  if (is500(error)) {
    return {
      loading: false,
      token: null,
      userDetails: null,
      error: AUTH_ERRORS.PAGE_ERROR,
    };
  }

  if (is401(error)) {
    return {
      loading: false,
      token: null,
      userDetails: null,
      error: AUTH_ERRORS.ACCESS_DENIED,
    };
  }

  return {
    loading: false,
    token: null,
    userDetails: null,
    error: AUTH_ERRORS.AUTH_FAILED,
  };
};

const acquireToken = async (instance, account) => {
  const request = { scopes: getBackendScopes(), account };

  try {
    return await instance.acquireTokenSilent(request);
  } catch {
    await instance.acquireTokenRedirect(request);
    return null;
  }
};

/**
 * Wraps the app with MSAL auth flow. Handles redirect callback, token acquisition,
 * and backend validation (AuthenticationClient.status) before rendering App.
 */
export default function MsalAuthWrapper() {
  const { instance, accounts, inProgress } = useMsal();
  const [authState, setAuthState] = useState({
    loading: true,
    token: null,
    userDetails: null,
    error: null,
  });
  const theme = getThemeByBrand(authState.userDetails?.brand);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    let cancelled = false;

    App.hideOrShow(true);

    const runAuth = async () => {
      try {
        if (accounts.length === 0) {
          await instance.loginRedirect({ scopes: getBackendScopes() });
          return;
        }

        // Register the active account synchronously, before any await, so
        // the axios interceptor can authenticate the very first request
        // (the /authorize call below).
        setMsalAccount(accounts[0]);

        const response = await acquireToken(instance, accounts[0]);
        if (!response) return;

        const token = response.accessToken;
        if (!isLikelyJwt(token)) {
          throw new Error(
            "MSAL returned a token that is not a valid JWT; check MSAL_BACKEND_APP configuration"
          );
        }

        const { request: authenticationRequest, salesCode } =
          buildAuthenticationRequest();

        const userDetails = await AuthenticationClient.status(
          authenticationRequest,
          { skipAuthFailureHandler: true }
        );

        if (cancelled) return;

        persistUserDetails(userDetails);
        userDetails.brand = resolveBrand(salesCode, userDetails);

        setAuthState({
          loading: false,
          token,
          userDetails,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        logMsg(error);
        setAuthState(mapErrorToState(error));
      } finally {
        if (!cancelled) App.hideOrShow(false);
      }
    };

    runAuth();

    return () => {
      cancelled = true;
    };
  }, [instance, accounts, inProgress]);

  if (authState.loading || inProgress !== InteractionStatus.None) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Loading />
      </ThemeProvider>
    );
  }

  if (authState.error === AUTH_ERRORS.PAGE_ERROR) {
    throw new Error(AUTH_ERRORS.PAGE_ERROR);
  }

  if (authState.error === AUTH_ERRORS.ACCESS_DENIED) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorScreen
          errorMessageKey="AuthAccessDeniedScreen.errorMessage"
          buttonTextKey="AuthAccessDeniedScreen.buttonText"
          titleKey="AuthAccessDeniedScreen.header"
        />
      </ThemeProvider>
    );
  }

  if (authState.error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ padding: 20, textAlign: "center" }}>
          <p>Authentication failed. Please try again.</p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App token={authState.token} userDetails={authState.userDetails} />
    </ThemeProvider>
  );
}
